import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { BarChartMarshalledGroup } from "@evergis/charts";
import { ITheme, ThemeProvider } from "@evergis/ui";

import { BarChart, TooltipContainer, TooltipDescription } from "./styled";

import { declension } from "../../utils/declension";

import { RangeStatisticsChartProps } from "./types";

export const BAR_WIDTH = 8;

export const RangeStatisticsChart: FC<RangeStatisticsChartProps> = ({
  data = [],
  width,
  height,
  markers,
  selectedRange,
  classifyData,
  onDrillDown,
}) => {
  const { t } = useTranslation("table");
  const theme = useTheme() as ITheme;

  const customXAxis = useCallback(
    xAxis => {
      xAxis.select(".tick:first-of-type").attr("transform", "translate(0, 0)");
      xAxis.select(".tick:last-of-type").attr("transform", `translate(${width}, 0)`);
    },
    [width],
  );

  const customXAxisBottom = useCallback((xAxisBottom, items) => {
    xAxisBottom.tickFormat((index: number) =>
      index === 0 || index === items.length - 1 ? items[index].groupName : "",
    );
  }, []);

  const customYAxis = useCallback(yAxis => {
    yAxis.remove();
  }, []);

  const customBars = useCallback(({ bars }) => {
    bars.attr("rx", 1).attr("ry", 1);
  }, []);

  const marshalledMap = useCallback(
    items => {
      return items.map(([item]: BarChartMarshalledGroup[], index: number) => [
        {
          ...item,
          color:
            selectedRange && index >= selectedRange[0] && index <= selectedRange[1]
              ? theme.palette.primary
              : theme.palette.elementDeep,
        },
      ]);
    },
    [selectedRange, theme.palette.elementDeep, theme.palette.primary],
  );

  return (
    <BarChart
      colors={{ value: theme.palette.primary }}
      width={width}
      data={data}
      markers={markers}
      height={height || 100}
      customXAxis={customXAxis}
      customXAxisBottom={xAxisBottom => customXAxisBottom(xAxisBottom, data)}
      customYAxis={customYAxis}
      xAxisPadding={0}
      yAxisPadding={0}
      customBars={customBars}
      dynamicTooltipEnable
      isBarTooltip
      renderTooltip={groups => {
        const group = groups[0];

        return (
          <ThemeProvider>
            <TooltipContainer>
              {group.value} {declension(t("feature1"), t("feature2"), t("feature3"), group.value)}
              {classifyData && (
                <TooltipDescription>
                  {`${classifyData.classifyResult[group.groupIndex].value} - ${
                    classifyData.classifyResult[group.groupIndex + 1].value
                  }`}
                </TooltipDescription>
              )}
            </TooltipContainer>
          </ThemeProvider>
        );
      }}
      barWidth={BAR_WIDTH}
      margin={{
        top: 0,
        left: 0,
        bottom: 20,
        right: 0,
      }}
      marshalledMap={marshalledMap}
      selectable
      onSelect={onDrillDown}
    />
  );
};
